import { useState, type FormEvent } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Clock, Headphones, Mail, MessageCircle, PlayCircle, Send, type LucideIcon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { CONTACT_INFO } from '@/lib/constants'
import { PRODUCTS, getProductById } from '@/lib/products'
import { cn } from '@/lib/utils'
import { AppLauncherLink } from '@/components/layout/AppLauncher'

// Endpoint is served same-origin by nginx, which injects the central-mail
// service key server-side and proxies to central-mail's /api/mail/send.
// The key is never exposed to the browser.
const CONTACT_ENDPOINT = '/api/contact'

const TOPICS: { key: string; icon: LucideIcon }[] = [
  { key: 'demo', icon: PlayCircle },
  { key: 'support', icon: Headphones },
  { key: 'waitlist', icon: Clock },
  { key: 'other', icon: MessageCircle },
]

// Language-independent labels for the e-mail subject line.
const SUBJECT_TOPIC: Record<string, string> = {
  demo: 'Demo',
  support: 'Support',
  waitlist: 'Waitlist',
  other: 'General',
}
const SUBJECT_PRODUCT: Record<string, string> = {
  bms: 'BMS',
  bell: 'School Bell',
  mosque: 'Mosque',
  crypto: 'Crypto',
  masar: 'Masar AI',
}

export default function Contact() {
  const { t } = useTranslation(['common', 'products'])
  const [params] = useSearchParams()

  const initialTopic = TOPICS.some((x) => x.key === params.get('topic')) ? params.get('topic')! : 'demo'
  const initialProduct = getProductById(params.get('product') ?? '')?.id ?? ''

  return (
    <>
      <Helmet>
        <title>{`${t('contact.meta.title')} | Tdrem`}</title>
        <meta name="description" content={t('contact.meta.description')} />
        <link rel="canonical" href="https://www.tdrem.com/contact" />
      </Helmet>

      <section className="relative -mt-16 overflow-hidden pt-16">
        <div aria-hidden className="bg-grid absolute inset-0 -z-10" />
        <div className="container grid grid-cols-1 gap-10 py-10 sm:py-14 md:py-20 lg:grid-cols-[1fr_1.35fr] lg:gap-16">
          {/* Side */}
          <div className="space-y-6 sm:space-y-8">
            <div className="space-y-3 sm:space-y-4">
              <span className="eyebrow">{t('nav.contact')}</span>
              <h1 className="text-3xl font-extrabold leading-tight sm:text-4xl md:text-5xl">{t('contact.title')}</h1>
              <p className="text-base text-muted-foreground sm:text-lg">{t('contact.subtitle')}</p>
            </div>

            <a href={`mailto:${CONTACT_INFO.email}`} className="surface flex items-center gap-4 p-4 transition-shadow hover:shadow-lift sm:p-5">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Mail className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm text-muted-foreground">{t('contact.emailUs')}</span>
                <span className="block truncate font-semibold">{CONTACT_INFO.email}</span>
              </span>
            </a>

            <div className="rounded-2xl border border-dashed p-5">
              <p className="font-semibold">{t('contact.customerTitle')}</p>
              <p className="mb-4 text-sm text-muted-foreground">{t('contact.customerBody')}</p>
              <ul className="space-y-1">
                {PRODUCTS.filter((p) => p.status !== 'comingSoon').map((p) => (
                  <li key={p.id}>
                    <AppLauncherLink product={p} className="rounded-xl p-2 hover:bg-accent" />
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Form — keyed by query defaults so navigating from another CTA updates selections */}
          <ContactForm key={`${initialTopic}:${initialProduct}`} initialTopic={initialTopic} initialProduct={initialProduct} />
        </div>
      </section>
    </>
  )
}

function ContactForm({ initialTopic, initialProduct }: { initialTopic: string; initialProduct: string }) {
  const { t } = useTranslation(['common', 'products'])
  const [topic, setTopic] = useState(initialTopic)
  const [product, setProduct] = useState<string>(initialProduct)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)

    const form = e.currentTarget
    const data = new FormData(form)

    // Honeypot — bots fill hidden fields; humans don't. Silently "succeed".
    if ((data.get('contact_ref') as string)?.trim()) {
      setSubmitted(true)
      return
    }

    const name = (data.get('name') as string)?.trim()
    const email = (data.get('email') as string)?.trim()
    const message = (data.get('message') as string)?.trim()

    if (!name || !email || !message) {
      setError(t('contact.form.error.required'))
      return
    }

    const productName = product ? SUBJECT_PRODUCT[product] ?? product : 'General'
    const topicName = SUBJECT_TOPIC[topic] ?? topic
    const payload = {
      name,
      email,
      subject: `[${topicName}] ${productName} — ${name}`,
      message,
    }

    setLoading(true)
    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`Request failed: ${res.status}`)
      setSubmitted(true)
      form.reset()
    } catch {
      setError(t('contact.form.error.generic'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="surface p-5 shadow-lift sm:p-6 md:p-8">
      {submitted ? (
        <div className="flex min-h-[420px] flex-col items-center justify-center text-center" role="status">
          <CheckCircle2 className="mb-4 h-14 w-14 text-emerald-500" />
          <h2 className="text-2xl font-bold">{t('contact.form.success.title')}</h2>
          <p className="mt-2 text-muted-foreground">{t('contact.form.success.description')}</p>
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            className="mt-6 text-sm font-medium text-primary hover:underline"
          >
            {t('contact.form.another')}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          {/* Honeypot */}
          <div aria-hidden className="sr-only">
            <label>
              Ref
              <input type="text" name="contact_ref" tabIndex={-1} autoComplete="off" />
            </label>
          </div>

          <fieldset>
            <legend className="mb-3 text-sm font-semibold">{t('contact.form.topic')}</legend>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {TOPICS.map(({ key, icon: Icon }) => (
                <label
                  key={key}
                  className={cn(
                    'flex cursor-pointer flex-col items-center gap-2 rounded-xl border p-3 text-center text-sm font-medium transition-all has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
                    topic === key ? 'border-primary bg-primary/5 text-primary' : 'hover:bg-accent'
                  )}
                >
                  <input
                    type="radio"
                    name="topic"
                    value={key}
                    checked={topic === key}
                    onChange={() => setTopic(key)}
                    className="sr-only"
                  />
                  <Icon className="h-5 w-5" />
                  {t(`contact.topics.${key}`)}
                </label>
              ))}
            </div>
          </fieldset>

          <div className="space-y-2">
            <Label htmlFor="product">{t('contact.form.product')}</Label>
            <select
              id="product"
              name="product"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              className="flex h-11 w-full rounded-xl border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">{t('contact.form.productNone')}</option>
              {PRODUCTS.map((p) => (
                <option key={p.id} value={p.id}>
                  {t(`products:${p.id}.name`)} · {t(`products:${p.id}.category`)}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">{t('contact.form.name')}</Label>
              <Input id="name" name="name" autoComplete="name" required className="h-11 rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('contact.form.email')}</Label>
              <Input id="email" name="email" type="email" autoComplete="email" dir="ltr" required className="h-11 rounded-xl" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">{t('contact.form.message')}</Label>
            <Textarea
              id="message"
              name="message"
              rows={5}
              required
              placeholder={t(`contact.placeholders.${topic}`)}
              className="rounded-xl"
            />
          </div>

          {error && (
            <p role="alert" className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            {loading ? t('contact.form.sending') : t('contact.form.send')}
            {!loading && <Send className="h-4 w-4 flip-rtl" />}
          </button>
        </form>
      )}
    </div>
  )
}
